import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { IcnSpinner } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';
import type { ButtonSize, ButtonVariant } from '../Button/index.js';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className'
> {
  /** Required — icon-only buttons have no visible text. */
  label: string;
  /** Announced while loading, if different from `label` (e.g. "Saving…"). */
  loadingLabel?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** When true, the button is a toggle — renders with `aria-pressed={active}`.
   *  When undefined/false, the button is NOT a toggle and omits `aria-pressed`,
   *  so screen readers announce it as a plain button. */
  toggle?: boolean;
  active?: boolean;
  loading?: boolean;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
  children: ReactNode;
}

const SPINNER_SIZE: Record<ButtonSize, number> = { sm: 12, md: 14, lg: 16 };

/** Icon-only button — requires `label` for screen readers. */
export function IconButton(props: IconButtonProps): ReactNode {
  const {
    label,
    loadingLabel,
    variant = 'ghost',
    size = 'md',
    toggle = false,
    active = false,
    loading = false,
    className,
    disabled,
    ref,
    children,
    type = 'button',
    ...rest
  } = props;

  const t = useI18nT();
  const isDisabled = disabled === true || loading;
  const classes = ['vq-icon-btn', className].filter(Boolean).join(' ');
  // When loading, screen readers announce either the caller-provided
  // loadingLabel, or the i18n fallback "处理中 / Loading". The plain
  // `label` stays as the tooltip so sighted users still see context.
  const announced = loading ? (loadingLabel ?? t('iconButton.loadingLabel')) : label;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={classes}
      data-component="IconButton"
      data-variant={variant}
      data-size={size}
      data-active={active ? 'true' : 'false'}
      data-state={loading ? 'loading' : isDisabled ? 'disabled' : 'idle'}
      disabled={isDisabled}
      aria-label={announced}
      aria-pressed={toggle ? active : undefined}
      aria-busy={loading || undefined}
      title={announced}
    >
      {loading ? <IcnSpinner size={SPINNER_SIZE[size]} /> : children}
    </button>
  );
}
